import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { PillButton } from "./PillButton";

export const homepageFaqs = [
  {
    q: "Is feedback really 100% anonymous?",
    a: "Yes. Database-level triggers strip every byte of PII (email, IP, name, role) before insert. Even our own platform owners can't identify a respondent.",
  },
  {
    q: "How is this different from Officevibe, Culture Amp, or Lattice?",
    a: "Those are survey platforms with anonymous mode bolted on. SendFeedback.ai is anonymous-first: PII is stripped at the database, AI runs the conversation, and you get themes and risk scoring without manual tagging.",
  },
  {
    q: "What about works councils and EU compliance?",
    a: "We're GDPR-compliant with EU data residency and a signed DPA on request. Because no PII is ever stored, works council reviews are typically straightforward — we have a template document available.",
  },
  {
    q: "How long does setup take?",
    a: "Most teams launch in under 5 minutes. Pick a plan, create a feedback page, share the link — that's it. No IT, no integrations required to start.",
  },
  {
    q: "Can I run pulse campaigns and one-off surveys?",
    a: "Both. Run continuous always-on feedback channels, scheduled pulses, or one-time campaigns — all anonymous, all in one inbox.",
  },
  {
    q: "Do you integrate with Slack, HRIS, or SSO?",
    a: "Yes — Slack notifications and HRIS integrations on Business+. SSO/SAML on Enterprise. Webhooks available on every paid plan.",
  },
  {
    q: "What happens if I exceed my plan's limits?",
    a: "Nothing breaks. We notify you and you can upgrade in one click. We never lock you out of feedback you've already received.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. No annual lock-in unless you opt for the yearly discount. Cancel from settings — keeps your data accessible for 30 days.",
  },
];

export const FAQAccordion = () => {
  const [open, setOpen] = useState<number | null>(0);
  const navigate = useNavigate();

  return (
    <section className="container mx-auto px-3 lg:px-8 py-16 lg:py-24" id="faq">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight">Frequently Asked Questions</h2>
        <p className="mt-4 text-muted-foreground">Can't find your answer? Visit our help center.</p>
        <div className="mt-6 flex justify-center">
          <PillButton variant="outline" size="md" onClick={() => navigate("/help")}>
            Go To Help Center
          </PillButton>
        </div>
      </div>

      <div className="max-w-3xl mx-auto space-y-3">
        {homepageFaqs.map((f, i) => {
          const isOpen = open === i;
          return (
            <div
              key={f.q}
              className={cn(
                "rounded-2xl border bg-card transition-all",
                isOpen ? "border-[hsl(var(--mkt-primary)/0.3)] mkt-shadow-card" : "border-border"
              )}
            >
              <button
                onClick={() => setOpen(isOpen ? null : i)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
              >
                <span className="text-base lg:text-lg font-semibold text-foreground">{f.q}</span>
                <ChevronDown
                  className={cn(
                    "h-5 w-5 text-muted-foreground shrink-0 transition-transform",
                    isOpen && "rotate-180 text-[hsl(var(--mkt-primary))]"
                  )}
                />
              </button>
              {isOpen && (
                <div className="px-6 pb-6 text-muted-foreground leading-relaxed">{f.a}</div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
